/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from 'react';
import { useEffect, useState } from 'react';
import MediaPrepHeaderSection from '../../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../../common/commonComponents/mediaPrepPageHeader';
import { ContainerInventoryService } from '../../../../../service/MediaContainerServices';
import StatusCard from '../partials/statistics';
import FullLifecycleAuditModal from "../partials/fullLifecycleAuditModal";
import { useTranslation } from 'react-i18next';

interface ContainerUnitDetailsProps {
  unitId: string;
  onBack: () => void;
}

const ContainerUnitDetails: React.FC<ContainerUnitDetailsProps> = ({
  unitId,
  onBack,
}) => {
  const { t } = useTranslation();
  const [showFullLogs, setShowFullLogs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [containerProfile, setContainerProfile] = useState<any>(null);

  useEffect(() => {
    const fetchContainerProfile = async () => {
      try {
        setLoading(true);

        const res =
          await ContainerInventoryService.getIndividualContainerDetails(
            unitId
          );

        if (res?.status) {
          setContainerProfile(res.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (unitId) {
      fetchContainerProfile();
    }
  }, [unitId]);


  return (
    <div className="media-prep-scope min-h-screen bg-[#F8FAFC] text-slate-900 pb-20">
    <MediaPrepHeaderSection />
    <MediaPrepPageHeader pageHeader="Unit Details"  />
      {/* Header section with back button */}
      <div className="sticky top-[110px] z-50 bg-white border-b border-slate-200 px-8 py-2 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-6">
          <button 
            onClick={onBack} 
            className="w-9 h-9 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center text-slate-400 hover:text-[#2D3E50] hover:border-slate-300 hover:shadow-md transition-all group"
          >
            <span className="material-symbols-outlined text-xl group-hover:-translate-x-0.5 transition-transform">{t("containerInventory.icons.arrow_back")}</span>
          </button>
          
          <div className="flex flex-col gap-0">
            <div className="flex items-center gap-4">
              <h1 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.18em]">
                {t("containerInventory.containerLotDetails.Lifecycle")}
              </h1>

            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-black text-[#2D3E50] tracking-tight">
                {containerProfile?.serial_number || "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button className="px-3 py-1 border border-slate-200 rounded-xl font-black text-[10px] text-slate-600 uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">{t("containerInventory.icons.print")}</span>
            Unit Tag
          </button>
        </div>
      </div>

      <div className="pt-3 px-10 pb-10 max-w-7xl mx-auto space-y-8">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
        {[
            { label: t("containerInventory.containerLotDetails.sterilizations"), val: (containerProfile?.total_sterilizations || 0).toString(), color: "rgb(255, 255, 255)", icon: "thermostat", trend: "Sterilization Cycles", status: "" },
            { label: t("containerInventory.containerLotDetails.usage"), val: (containerProfile?.total_usage_cycles || 0).toString(), color: "rgb(255, 255, 255)", icon: "sync", trend: "Usage Count", status: "" },
            { label: t("containerInventory.containerLotDetails.remainingUsage"), val: (containerProfile?.remaining_usage || 0).toString(), color: "rgb(255, 255, 255)", icon: "hourglass_top", trend: "Life Remaining", status: "Avaiable" },
            { label: t("containerInventory.containerLotDetails.lastUsedDate"), val: containerProfile?.last_activity_date?.split(" ")[0] || "-", color: "rgb(255, 255, 255)", icon: "calendar_today", trend: "Last Activity", status: "" },
        ].map((box, i) => (
            <StatusCard
              key={i}
              label={box.label}
              value={box.val}
              color={box.color}
              icon={box.icon}
              trend={box.trend}
              status={box.status}
              selectedStatus=""
            />
          ))}
        </div>

        <div className="grid grid-cols-12 gap-8">
          {/* Left Column: Info & Analytics */}
          <div className="col-span-12 lg:col-span-6 space-y-8">
            
            {/* Container Info Card */}
            <section className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-blue-500">{t("containerInventory.icons.info")}</span>
                  <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t("containerInventory.containerLotDetails.containerInfo")}</h3>
                </div>
              </div>
              <div className="p-8 grid grid-cols-3 gap-y-8">
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("containerInventory.containerLotDetails.ID")}</p>
                  <p className="text-sm font-black text-slate-700">{containerProfile?.serial_number || "-"}</p>
                </div>
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("containerInventory.containerLotDetails.type")}</p>
                  <p className="text-sm font-black text-slate-700">{containerProfile?.container_type || "-"}</p>
                </div>
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("containerInventory.containerLotDetails.Material")}</p>
                  <p className="text-sm font-black text-slate-700">{containerProfile?.container_category || "-"}</p>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column: Timeline / Activity */}
          <div className="col-span-14 lg:col-span-5 space-y-6">
            <section className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm h-full overflow-hidden flex flex-col">
              <div className="px-8 py-3 border-b border-slate-50 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-slate-400">{t("containerInventory.icons.history")}</span>
                  <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t("containerInventory.containerLotDetails.HistoryTimeline")}</h3>
                </div>
                <div className="w-[100px] text-right">
                  <button
                    onClick={() => setShowFullLogs(true)}
                    className="text-[9px] font-black text-blue-600 uppercase whitespace-nowrap bg-transparent border-0 shadow-none hover:underline"
                  >
                    {t("containerInventory.containerLotDetails.viewLog")}
                  </button>
                </div>
              </div>
              
              <div className="px-6 pt-2 pb-4">
                <div className="space-y-2 relative before:absolute before:left-5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
                  {containerProfile?.history_timeline?.map((event: any, idx: number) => (
                    <div key={idx} className="relative pl-12">
                      <div className="absolute left-0 top-0 w-8 h-8 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center text-slate-400 z-10 transition-all hover:border-slate-300">
                        <span className="material-symbols-outlined text-sm">
                          {event.event_category === "dispensing usage"
                            ? "science"
                            : event.event_category === "wash and cleanup"
                            ? "cleaning_services"
                            : event.event_category === "sterilization cycle"
                            ? "thermostat"
                            : event.event_category === "post_autoclave physical inspection"
                            ? "fact_check"
                            : "science"}
                        </span>
                      </div>
                      <div className="space-y-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-black text-slate-700 uppercase tracking-tight">{event.event_category || "-"}</h4>
                          <span className="mr-4 text-[9px] font-black text-slate-400">{event.event_date?.split(' ')[0] || "-"}</span>
                        </div>
                        <div className="flex items-start gap-1.5 leading-none">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 mt-[2px] shrink-0"></span>
                          <p className="text-[10px] font-bold text-slate-500 italic leading-none">Result: {event.result}</p>
                        </div>
                        <div className="flex items-start gap-1.5 -mt-5">
                          <div className="w-3 h-3 flex items-center justify-center text-slate-400 mt-[1px] shrink-0">
                            <span className="material-symbols-outlined text-[10px]">{t("containerInventory.icons.person")}</span>
                          </div>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-tight leading-none">{event.performed_by}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-0 px-5 pb-3 border-t border-slate-50 bg-slate-50/30">
                 <div className="bg-white px-4 py-1 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3">
                    <div className="w-6 h-6 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                      <span className="material-symbols-outlined text-2xl font-black">{t("containerInventory.icons.assignment_turned_in")}</span>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("containerInventory.containerLotDetails.currentStatus")}</p>
                      <h4 className="text-sm font-black text-emerald-600 tracking-tight">{containerProfile?.current_status || "-"}</h4>
                    </div>
                 </div>
              </div>
            </section>
          </div>
        </div>

        {/* Full Logs Modal Overlay */}
        <FullLifecycleAuditModal
          show={showFullLogs}
          onClose={() => setShowFullLogs(false)}
          containerProfile={containerProfile}
        />
      </div>
    </div>
  );
};

export default ContainerUnitDetails;
